import React, { useState, useEffect, useCallback } from 'react';

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };

function PmsCorporateCreditRisk({ token, selectedProperty }) {
  const [scores, setScores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const loadScores = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/ai/corporate-risk/scores', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setScores(data.scores || []);
      setSummary(data.summary || null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty?.id, headers]);

  useEffect(() => { loadScores(); }, [loadScores]);

  const generate = async () => {
    setGenerating(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/ai/corporate-risk/generate', { method: 'POST', headers: headers(true), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      setMessage(`Scored ${data.scored_count} corporate accounts`);
      setTimeout(() => setMessage(''), 4000);
      await loadScores();
    } catch (err) { setError(err.message); }
    finally { setGenerating(false); }
  };

  if (!selectedProperty?.id) {
    return (<div style={{ padding: '1rem', maxWidth: 1000 }}><h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Corporate Credit Risk</h2><p style={{ color: 'var(--theme-text-muted)' }}>Please select a property.</p></div>);
  }

  const formatCurrency = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
  const riskColors = { GREEN: 'var(--theme-success)', AMBER: '#f59e0b', RED: 'var(--theme-danger)' };
  const riskBg = { GREEN: '#dcfce7', AMBER: '#fef3c7', RED: '#fee2e2' };

  return (
    <div style={{ padding: '1rem', maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ marginTop: 0, color: 'var(--theme-primary)', marginBottom: 4 }}>Corporate Credit Risk Scorecard</h2>
          <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: 0 }}>AI-powered risk scoring based on payment behavior and credit utilization</p>
        </div>
        <button onClick={generate} disabled={generating} style={{
          padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: generating ? 'var(--theme-text-muted)' : '#f97316',
          color: '#fff', fontWeight: 600, cursor: generating ? 'wait' : 'pointer', fontSize: '0.85rem',
        }}>{generating ? 'Scoring...' : 'Score All Corporates'}</button>
      </div>

      {error && <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginTop: '0.5rem' }}>{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : scores.length === 0 ? (
        <div style={sectionStyle}><div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--theme-text-muted)' }}>
          <div style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>No risk scores available</div>
          <div style={{ fontSize: '0.85rem' }}>Click "Score All Corporates" to analyze corporate credit health.</div>
        </div></div>
      ) : (
        <>
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
              <SCard label="Total" value={summary.total} color="var(--theme-primary)" />
              <SCard label="Green (Healthy)" value={summary.green} color="var(--theme-success)" />
              <SCard label="Amber (Monitor)" value={summary.amber} color="#f59e0b" />
              <SCard label="Red (Restrict)" value={summary.red} color="var(--theme-danger)" />
              <SCard label="Total Overdue" value={formatCurrency(summary.total_overdue)} color="var(--theme-danger)" />
            </div>
          )}

          <div style={{ ...sectionStyle, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: 'var(--theme-border-light)', borderBottom: '2px solid var(--theme-border)' }}>
                  <th style={thStyle}>Corporate</th>
                  <th style={thStyle}>Risk</th>
                  <th style={thStyle}>Score</th>
                  <th style={thStyle}>Credit Utilization</th>
                  <th style={thStyle}>Limit</th>
                  <th style={thStyle}>Used</th>
                  <th style={thStyle}>Overdue</th>
                  <th style={thStyle}>Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {scores.map(s => {
                  const factors = typeof s.risk_factors === 'string' ? JSON.parse(s.risk_factors) : (s.risk_factors || []);
                  const isExpanded = expandedId === s.id;
                  return (
                    <React.Fragment key={s.id}>
                      <tr onClick={() => setExpandedId(isExpanded ? null : s.id)}
                        style={{ cursor: 'pointer', borderBottom: '1px solid var(--theme-border-light)', background: s.risk_level === 'RED' ? '#fff5f5' : '#fff' }}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600 }}>{s.corporate_name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>{s.corporate_code}</div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontWeight: 700, fontSize: '0.75rem', background: riskBg[s.risk_level], color: riskColors[s.risk_level] }}>
                            {s.risk_level}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: riskColors[s.risk_level] }}>{s.risk_score}/100</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <div style={{ width: 60, height: 8, background: 'var(--theme-border)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(100, s.credit_utilization || 0)}%`, height: '100%', background: riskColors[s.risk_level], borderRadius: 4 }} />
                            </div>
                            <span style={{ fontSize: '0.78rem', color: '#475569' }}>{Number(s.credit_utilization || 0).toFixed(0)}%</span>
                          </div>
                        </td>
                        <td style={tdStyle}>{formatCurrency(s.credit_limit)}</td>
                        <td style={tdStyle}>{formatCurrency(s.credit_used)}</td>
                        <td style={tdStyle}>
                          {Number(s.overdue_invoices) > 0 && <span style={{ color: 'var(--theme-danger)', fontWeight: 600 }}>{s.overdue_invoices} ({formatCurrency(s.total_overdue_amount)})</span>}
                          {Number(s.overdue_invoices) === 0 && <span style={{ color: 'var(--theme-success)' }}>None</span>}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 200, whiteSpace: 'normal', fontSize: '0.8rem', color: '#475569' }}>{s.recommendation}</td>
                      </tr>
                      {isExpanded && factors.length > 0 && (
                        <tr>
                          <td colSpan={8} style={{ padding: '0.75rem 1rem', background: 'var(--theme-bg-light)' }}>
                            <div style={{ fontWeight: 600, marginBottom: '0.4rem', color: '#475569', fontSize: '0.82rem' }}>Risk Factors</div>
                            {factors.map((f, i) => (
                              <div key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8rem', padding: '0.2rem 0', alignItems: 'center' }}>
                                <span style={{ width: 24, height: 24, borderRadius: '50%', background: f.impact > 6 ? '#fee2e2' : f.impact > 3 ? '#fef3c7' : 'var(--theme-border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: f.impact > 6 ? 'var(--theme-danger)' : '#475569', flexShrink: 0 }}>{f.impact}</span>
                                <span><strong>{f.factor}:</strong> {f.detail}</span>
                              </div>
                            ))}
                            {s.avg_days_to_payment && <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', marginTop: '0.3rem' }}>Avg days to payment: {s.avg_days_to_payment} | Total bookings: {s.total_bookings} | Active: {s.active_bookings}</div>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = { padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' };
const tdStyle = { padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' };

function SCard({ label, value, color }) {
  return (<div style={{ background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '0.75rem 1rem', textAlign: 'center' }}>
    <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.3rem', fontWeight: 700, color }}>{value}</div>
  </div>);
}

export default PmsCorporateCreditRisk;
